// Tracks the last match the player was inside so that a refresh or
// connection drop can offer a one-click rejoin instead of silently
// returning them to the lobby. Persists to localStorage with a short
// TTL — after ~5 minutes the match is almost certainly over, so we
// drop the hint rather than dumping the player into an empty room.

const KEY = 'smp:lastMatch:v1';
const SNAP_KEY = 'smp:lastMatch:selfSnap:v1';
const TTL_MS = 5 * 60 * 1000;

// Dev-only rejoin logger. Every callsite wraps the invocation in
// `if (import.meta.env.DEV)` so the arg expression (object literals,
// string keys) gets dead-code-eliminated in prod. The helper body is
// also gated for belt-and-suspenders. No `[rejoin]` strings ship.
const DEV = import.meta.env.DEV;
const rejoinLog = (...a: unknown[]): void => {
  if (DEV) console.info('[rejoin]', ...a);
};

// Loosely-typed snapshot of the local player at disconnect time. Used
// to hydrate the SoloStub on rejoin (solo mode has no server to hold
// state). MP sessions ignore this — the real server already restores
// state via deviceId-keyed bot reservation.
export type SelfSnapshotStash = {
  room: string;
  at: number;
  snap: {
    hp: number;
    hpMax: number;
    level: number;
    xp: number;
    xpNeeded: number;
    ultCharge: number;
    ultActive: boolean;
    kills: number;
    comboTier: number;
    powerups: unknown[];
    legendary: unknown;
    mythic: unknown;
    viralUntilSec: number;
    mindMeldUntilSec: number;
    role: string;
    x: number;
    z: number;
  };
};

export function rememberSelfSnapshot(room: string, snap: SelfSnapshotStash['snap']): void {
  try {
    const payload: SelfSnapshotStash = { room, at: Date.now(), snap };
    localStorage.setItem(SNAP_KEY, JSON.stringify(payload));
  } catch { /* noop */ }
}

export function forgetSelfSnapshot(): void {
  try { localStorage.removeItem(SNAP_KEY); } catch { /* noop */ }
}

export function loadSelfSnapshot(room: string): SelfSnapshotStash | null {
  try {
    const raw = localStorage.getItem(SNAP_KEY);
    if (!raw) {
      if (DEV) rejoinLog('loadSelfSnapshot', { room, hit: false, reason: 'no stash' });
      return null;
    }
    const parsed = JSON.parse(raw) as SelfSnapshotStash;
    if (!parsed || parsed.room !== room || typeof parsed.at !== 'number') {
      if (DEV) rejoinLog('loadSelfSnapshot', { room, hit: false, reason: 'room mismatch', stashRoom: parsed?.room });
      return null;
    }
    if (Date.now() - parsed.at > TTL_MS) {
      localStorage.removeItem(SNAP_KEY);
      if (DEV) rejoinLog('loadSelfSnapshot', { room, hit: false, reason: 'expired' });
      return null;
    }
    if (DEV) rejoinLog('loadSelfSnapshot', { room, hit: true, level: parsed.snap.level, hp: parsed.snap.hp });
    return parsed;
  } catch (e) {
    if (DEV) rejoinLog('loadSelfSnapshot', { room, hit: false, reason: 'throw', err: String(e) });
    return null;
  }
}

export type LastMatch = {
  room: string;       // PartyKit room id to re-swap into
  label: string;      // Human-readable label shown in the prompt
  at: number;         // ms epoch when the player joined
};

export function rememberMatch(room: string, label: string): void {
  try {
    const payload: LastMatch = { room, label, at: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Private browsing / quota — silently drop. The prompt just won't
    // appear next session; no other behaviour is affected.
  }
}

export function forgetMatch(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(SNAP_KEY);
  } catch { /* noop */ }
}

export function loadRecentMatch(): LastMatch | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastMatch;
    if (!parsed || typeof parsed.room !== 'string' || typeof parsed.at !== 'number') {
      localStorage.removeItem(KEY);
      return null;
    }
    if (Date.now() - parsed.at > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Renders a dismissable toast-style prompt in the top-centre of the
// lobby offering a one-click rejoin. Shows a live countdown of the
// remaining stash TTL — when it hits zero the prompt removes itself
// since the server would no longer recognise the reservation. Caller
// gets a close handle for early teardown (e.g. on scene swap).
export function promptRejoinMatch(
  saved: LastMatch,
  onRejoin: (room: string) => void,
  parent: HTMLElement = document.body,
): () => void {
  const wrap = document.createElement('div');
  wrap.style.cssText = `
    position: fixed;
    top: 18px; left: 50%; transform: translateX(-50%);
    z-index: 9500;
    background: linear-gradient(135deg, rgba(15,23,42,.96), rgba(30,41,59,.96));
    border: 1.5px solid #fbbf24;
    border-radius: 12px;
    padding: 14px 22px;
    color: #fef3c7;
    font: 700 13px system-ui, sans-serif;
    letter-spacing: .5px;
    box-shadow: 0 18px 48px rgba(0,0,0,.7), 0 0 32px rgba(251,191,36,.35);
    display: flex; align-items: center; gap: 16px;
    pointer-events: auto;
    opacity: 0;
    transition: opacity .25s ease-out, transform .25s ease-out;
  `;
  wrap.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:4px;">
      <span style="font:900 14px system-ui;letter-spacing:1.5px;color:#fde047;">MATCH IN PROGRESS</span>
      <span style="opacity:.8;font-size:11px;">You were in <b style="color:#fff;">${escapeHtml(saved.label)}</b> — rejoin?</span>
    </div>
    <button data-act="rejoin" style="
      padding: 8px 16px; border-radius: 8px; border: none;
      background: linear-gradient(135deg,#fbbf24,#f59e0b); color: #1a1a1a;
      cursor: pointer; font: 900 12px system-ui; letter-spacing: 1.2px;
      box-shadow: 0 4px 12px rgba(251,191,36,.45);
      display: flex; flex-direction: column; align-items: center; gap: 2px;
      min-width: 112px;
    ">
      <span>REJOIN</span>
      <span data-countdown style="font:700 10px ui-monospace;letter-spacing:1px;opacity:.85;">5:00</span>
    </button>
    <button data-act="dismiss" style="
      padding: 8px 14px; border-radius: 8px;
      border: 1px solid rgba(148,163,184,.4);
      background: rgba(30,41,59,.6); color: #cbd5e1;
      cursor: pointer; font: 700 11px system-ui; letter-spacing: 1px;
    ">DISMISS</button>
  `;
  parent.appendChild(wrap);
  requestAnimationFrame(() => {
    wrap.style.opacity = '1';
    wrap.style.transform = 'translateX(-50%) translateY(4px)';
  });

  const countdownEl = wrap.querySelector<HTMLSpanElement>('[data-countdown]')!;

  let closed = false;
  const close = (rejoin: boolean) => {
    if (closed) return;
    closed = true;
    if (tickHandle) clearInterval(tickHandle);
    wrap.style.opacity = '0';
    wrap.style.transform = 'translateX(-50%) translateY(-8px)';
    setTimeout(() => wrap.remove(), 250);
    if (rejoin) onRejoin(saved.room);
    else forgetMatch();
  };

  // Countdown ticker — updates the pill inside the REJOIN button at
  // 1 Hz with the remaining TTL as m:ss. When it crosses zero we
  // drop the prompt (forgetMatch) since the server-side bot
  // reservation would be gone anyway.
  function renderCountdown() {
    const remainMs = Math.max(0, TTL_MS - (Date.now() - saved.at));
    if (remainMs <= 0) {
      close(false);
      return;
    }
    const totalSec = Math.floor(remainMs / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    countdownEl.textContent = `${m}:${String(s).padStart(2, '0')}`;
  }
  renderCountdown();
  const tickHandle: ReturnType<typeof setInterval> = setInterval(renderCountdown, 500);

  wrap.querySelector<HTMLButtonElement>('[data-act="rejoin"]')!
    .addEventListener('click', () => close(true));
  wrap.querySelector<HTMLButtonElement>('[data-act="dismiss"]')!
    .addEventListener('click', () => close(false));

  return () => close(false);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;'  :
    c === '>' ? '&gt;'  :
    c === '"' ? '&quot;' : '&#39;'
  ));
}
